<template>
    <div
        v-if="loaded && (loadError || providerRows.length > 0)"
        data-testid="profile-sign-in-section"
        class="flex flex-col gap-6 rounded-xl border border-taupe-200 bg-white p-6 sm:p-7"
    >
        <div>
            <h3 class="text-lg font-medium leading-6 text-taupe-800">
                How you sign in
            </h3>
            <p class="mt-1 max-w-2xl text-sm text-taupe-500">
                Connect GitHub or Google to sign in without typing a password.
            </p>
        </div>

        <div
            v-if="loadError"
            class="flex max-w-2xl items-center justify-between gap-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
            role="alert"
            data-testid="profile-sign-in-error"
        >
            <span>We couldn't load how you sign in.</span>
            <zen-button size="small" variant="secondary" @click="load">
                Try again
            </zen-button>
        </div>

        <ul
            v-else
            class="max-w-2xl divide-y divide-taupe-200 rounded-lg border border-taupe-200"
        >
            <li class="flex items-center gap-4 px-4 py-3">
                <div
                    class="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-taupe-100 text-taupe-600"
                    aria-hidden="true"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        class="h-5 w-5"
                    >
                        <path
                            fill-rule="evenodd"
                            d="M10 1a4.5 4.5 0 00-4.5 4.5V9H5a2 2 0 00-2 2v6a2 2 0 002 2h10a2 2 0 002-2v-6a2 2 0 00-2-2h-.5V5.5A4.5 4.5 0 0010 1zm3 8V5.5a3 3 0 10-6 0V9h6z"
                            clip-rule="evenodd"
                        />
                    </svg>
                </div>
                <div class="min-w-0 flex-1">
                    <p class="text-sm font-medium text-taupe-800">
                        Email and password
                    </p>
                    <p
                        class="text-sm text-taupe-500"
                        data-testid="sign-in-password-status"
                    >
                        {{
                            methods.has_password
                                ? 'You can sign in with your email and password.'
                                : "You haven't set a password."
                        }}
                    </p>
                </div>
            </li>

            <li
                v-for="row in providerRows"
                :key="row.provider"
                class="flex items-start gap-4 px-4 py-3"
                :data-testid="`sign-in-${row.provider}`"
            >
                <div
                    class="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-taupe-100 text-taupe-900"
                >
                    <provider-icon :provider="row.provider" class="h-5 w-5" />
                </div>
                <div class="min-w-0 flex-1">
                    <p class="text-sm font-medium text-taupe-800">
                        {{ label(row.provider) }}
                    </p>
                    <template v-if="row.identity">
                        <p class="break-words text-sm text-taupe-500">
                            <span v-if="row.identity.email"
                                >{{ row.identity.email }} &middot;
                            </span>
                            Connected
                            <zen-date-time
                                :value="row.identity.linked_at"
                                format="date"
                            />
                            <template v-if="row.identity.last_used_at">
                                &middot; Last used
                                <zen-date-time
                                    :value="row.identity.last_used_at"
                                    human
                                />
                            </template>
                        </p>
                        <p
                            v-if="!row.identity.can_unlink"
                            class="mt-1 text-sm text-taupe-500"
                            :data-testid="`sign-in-${row.provider}-locked`"
                        >
                            {{ lockedReason(row.provider) }}
                        </p>
                    </template>
                    <p v-else class="text-sm text-taupe-500">Not connected.</p>
                </div>
                <zen-button
                    v-if="row.identity"
                    size="small"
                    variant="secondary"
                    :aria-label="`Disconnect ${label(row.provider)}`"
                    :disabled="!row.identity.can_unlink"
                    @click="askToDisconnect(row.provider)"
                >
                    Disconnect
                </zen-button>
                <zen-button
                    v-else
                    size="small"
                    variant="secondary"
                    :aria-label="`Connect ${label(row.provider)}`"
                    :disabled="connecting !== '' && connecting !== row.provider"
                    :processing="connecting === row.provider"
                    processing-label="Connecting…"
                    @click="connect(row.provider)"
                >
                    Connect
                </zen-button>
            </li>
        </ul>

        <p
            v-if="!loadError && !methods.has_password"
            class="max-w-2xl text-sm text-taupe-500"
            data-testid="sign-in-no-password-note"
        >
            Want a password too? Sign out, then use "Forgot your password?" on
            the sign-in screen to set one.
        </p>

        <!-- Out of flow, so the open dialog's wrapper doesn't add a row to the
             card's gap and nudge the page down while it is shown. -->
        <div class="absolute">
            <disconnect-social-identity-modal
                :is-open="disconnectTarget !== ''"
                :provider="disconnectTarget"
                :processing="disconnecting"
                @close="closeDisconnect"
                @confirm="disconnect"
            />
        </div>
    </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import ProviderIcon from '@/screens/_components/social/ProviderIcon.vue'
import DisconnectSocialIdentityModal from '@/screens/_components/profile/modals/DisconnectSocialIdentityModal.vue'
import ProfileSocialService, {
    emptySignInMethods,
} from '@/services/profile.social.service'
import {
    SOCIAL_PROVIDERS,
    readSocialReturn,
    socialLinkMessage,
    socialProviderLabel,
    withoutSocialReturn,
} from '@/helpers/social-login'
import {
    errorMessage,
    infoMessage,
    successMessage,
} from '@/utils/notifications'

/**
 * "How you sign in" on the profile: the password, each connected GitHub or
 * Google identity (with Disconnect), and the providers that can still be
 * connected (with Connect).
 *
 * Connecting leaves the app: the API answers with the provider's consent-screen
 * URL, the window goes there, and the provider sends the browser back to
 * `/profile?social=<outcome>&provider=<provider>`, which is turned into a
 * toast here. Renders nothing while there is nothing to show: no connected
 * identities and nothing that can be connected (the feature is off).
 */
const route = useRoute()
const router = useRouter()

const methods = ref(emptySignInMethods())
const loaded = ref(false)
const loadError = ref(false)
// '' is "none": nothing is connecting, no dialog is open.
const connecting = ref('')
const disconnectTarget = ref('')
const disconnecting = ref(false)

const label = socialProviderLabel

const providerRows = computed(() =>
    SOCIAL_PROVIDERS.flatMap((provider) => {
        const identity = methods.value.identities.find(
            (candidate) => candidate.provider === provider
        )

        if (identity) {
            return [{ provider, identity }]
        }

        return methods.value.linkable_providers.includes(provider)
            ? [{ provider, identity: null }]
            : []
    })
)

/**
 * Why Disconnect is greyed out: the API won't remove the last way to sign in.
 */
function lockedReason(provider) {
    const others = methods.value.linkable_providers
        .filter((candidate) => candidate !== provider)
        .map((candidate) => label(candidate))

    return others.length > 0
        ? `This is the only way you can sign in right now. Connect ${others.join(' or ')} first, then you can disconnect ${label(provider)}.`
        : "This is the only way you can sign in right now, so it can't be disconnected."
}

async function load() {
    loadError.value = false

    // A rejection is handled here, so this never throws and callers can fire
    // and forget it.
    await ProfileSocialService.list().then(
        (result) => {
            methods.value = result
        },
        () => {
            loadError.value = true
        }
    )

    loaded.value = true
}

async function connect(provider) {
    if (connecting.value !== '') {
        return
    }

    connecting.value = provider

    try {
        const url = await ProfileSocialService.connect(provider)

        // The URL is the provider's consent screen, built by the API. Only
        // ever follow a web address, never anything else a response might say.
        if (!/^https?:\/\//i.test(url ?? '')) {
            throw new Error(
                `Something went wrong connecting ${label(provider)}. Try again.`
            )
        }

        globalThis.window.location.assign(url)
    } catch (error) {
        connecting.value = ''
        errorMessage(error)
        await load()
    }
}

function askToDisconnect(provider) {
    disconnectTarget.value = provider
}

function closeDisconnect() {
    if (!disconnecting.value) {
        disconnectTarget.value = ''
    }
}

async function disconnect() {
    const provider = disconnectTarget.value
    if (provider === '' || disconnecting.value) {
        return
    }

    disconnecting.value = true

    try {
        await ProfileSocialService.disconnect(provider)
        successMessage(`${label(provider)} is disconnected.`)
    } catch (error) {
        errorMessage(error)
    } finally {
        disconnecting.value = false
        disconnectTarget.value = ''
    }

    // Whether it worked or not, what the API says now is the truth.
    await load()
}

/**
 * Tell the person how connecting went, once, when the provider sent them back.
 */
function showSocialReturn() {
    const socialReturn = readSocialReturn(route.query)
    if (socialReturn === null) {
        return
    }

    const message = socialLinkMessage(
        socialReturn.outcome,
        socialReturn.provider
    )

    if (message.type === 'success') {
        successMessage(message.text)
    } else if (message.type === 'warning') {
        infoMessage(message.text)
    } else {
        errorMessage(message.text)
    }

    // Drop the outcome from the address so a refresh doesn't replay it.
    router.replace({ query: withoutSocialReturn(route.query) }).catch(() => {
        // Ignore navigation duplication failures.
    })
}

// Coming back with the browser's Back button can restore this page from the
// back/forward cache with Connect still spinning.
function onPageShow(event) {
    if (event.persisted) {
        connecting.value = ''
    }
}

onMounted(() => {
    globalThis.window.addEventListener('pageshow', onPageShow)
    showSocialReturn()
    void load()
})

onBeforeUnmount(() => {
    globalThis.window.removeEventListener('pageshow', onPageShow)
})
</script>
